/** App identity helpers shared by the app switcher, the App filter field and chips. */
import type { FocusApp, FocusEvidence } from './api';
import type { EvidenceLevel } from '@/components/ui/tag';

export interface AppIdentity {
  id: string;
  name: string;
  os: string;
  iconUrl: string | null;
}

/** "Android", "iOS" — the API spells OS in several ways. */
export const osLabel = (os: string): string => {
  const lower = os.trim().toLowerCase();
  if (lower === 'ios') return 'iOS';
  if (lower === 'android') return 'Android';
  return os;
};

/** An app named by a URL but not in the entitled list still gets a readable identity. */
export const identityOf = (id: string, apps: readonly FocusApp[] | undefined): AppIdentity => {
  const app = apps?.find((a) => a.id === id);
  if (app) return { id, name: app.name, os: osLabel(app.os), iconUrl: app.icon_url };
  const [appId = id, os = ''] = id.split('::');
  return { id, name: appId, os: osLabel(os), iconUrl: null };
};

/** The evidence label wording is fixed by the decision record (2026-09-30). */
export const EVIDENCE: Record<FocusEvidence, { level: EvidenceLevel; label: string }> = {
  enough: { level: 'enough', label: 'Enough data' },
  enough_at_28: { level: 'partial', label: 'Enough data at 28 days' },
  not_enough: { level: 'not-enough', label: 'Not enough data yet' },
};
